import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganisationContext } from '../context';

export type IncidentHeadingKey =
  | 'tags'
  | 'categories'
  | 'severities'
  | 'statuses';

type IncidentHeadingMap = Record<IncidentHeadingKey, string>;

const ATTRIBUTE_KEY_MAP: Record<IncidentHeadingKey, string> = {
  tags: 'incident_tags_label',
  categories: 'incident_categories_label',
  severities: 'incident_severities_label',
  statuses: 'incident_statuses_label',
};

export const useIncidentHeadings = (): IncidentHeadingMap => {
  const organisation = useContext(OrganisationContext);
  const { t } = useTranslation();

  const defaults = useMemo<IncidentHeadingMap>(
    () => ({
      tags: t('Tags'),
      categories: t('Categories'),
      severities: t('Severities'),
      statuses: t('Statuses'),
    }),
    [t]
  );

  return useMemo(() => {
    const attributes = (organisation?.attributes ??
      {}) as Record<string, unknown>;

    const resolveLabel = (key: IncidentHeadingKey) => {
      const attributeKey = ATTRIBUTE_KEY_MAP[key];
      const value = attributes?.[attributeKey];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
      return defaults[key];
    };

    return {
      tags: resolveLabel('tags'),
      categories: resolveLabel('categories'),
      severities: resolveLabel('severities'),
      statuses: resolveLabel('statuses'),
    };
  }, [organisation?.attributes, defaults]);
};

export type IncidentHeadings = ReturnType<typeof useIncidentHeadings>;


